/**
 * Texts of screen E11 · Account (specs/S8-account-tab/spec.md §4; mockups `Compte.html`,
 * `CompteDeconnexion.html`). Provisional until Q3 (tone): French uses "tu". Failure texts come
 * from the C2 catalogue by reason code, never from here (I12). Plural keys follow i18next: `_zero`
 * for a count of 0 in every language, then the language's `_one` / `_other`. No phone (A-D6, Q101).
 */
export const accountFr = {
  title: 'Compte',
  loading: 'Chargement…',
  retry: 'Réessayer',
  cancel: 'Annuler',
  telegram: {
    heading: 'Telegram',
    connected: 'Connecté',
    disconnected: 'Déconnecté',
    lost: 'Connexion perdue',
    disconnectedText: 'FXMate ne lit plus tes canaux : la copie est arrêtée.',
    // S8 R19, Q113: GET /me hides the missing steps while the account is blocked.
    unknownText:
      'L’état de ta connexion Telegram n’est pas disponible tant que ton compte de trading est injoignable.',
    logout: 'Se déconnecter de Telegram',
    signIn: 'Me reconnecter',
    loggedOut: 'Tu es déconnecté de Telegram. La copie est arrêtée.',
  },
  logoutSheet: {
    title: 'Se déconnecter de Telegram ?',
    noRead: 'FXMate ne pourra plus lire tes canaux.',
    stops_zero: 'Toute la copie s’arrête immédiatement.',
    stops_one: 'Toute la copie s’arrête immédiatement, sur ton canal.',
    stops_other: 'Toute la copie s’arrête immédiatement, sur tes {{count}} canaux.',
    resume: 'Pour reprendre la copie plus tard, tu devras te reconnecter avec un code Telegram.',
    confirm: 'Me déconnecter et arrêter la copie',
  },
  // S8 R5, R8 (Q31, Q85): the positions stay at the broker; nothing is said about later closes.
  openPositions_zero: 'Tu n’as aucune position ouverte par FXMate.',
  openPositions_one:
    'Tu as 1 position ouverte par FXMate : elle reste ouverte chez ton courtier, FXMate ne la clôture pas.',
  openPositions_other:
    'Tu as {{count}} positions ouvertes par FXMate : elles restent ouvertes chez ton courtier, FXMate ne les clôture pas.',
  tradingAccount: {
    heading: 'Compte de trading',
    summary: '{{platform}} · ••••{{last4}}',
    details: '{{server}} · {{holder}} · {{currency}}',
    demo: 'Compte démo',
    consent: 'Ordres autorisés le {{date}} à {{time}}',
    none: 'Aucun compte de trading',
    connect: 'Relier un compte',
    pending: 'La connexion de ton compte n’est pas terminée.',
    finish: 'Terminer la connexion',
    reconnect: 'Reconnecter mon compte',
    disconnect: 'Déconnecter ce compte',
    disconnected: 'Compte de trading déconnecté. La copie est arrêtée sur tous tes canaux.',
  },
  disconnectSheet: {
    title: 'Déconnecter ce compte ?',
    stops: 'La copie s’arrête sur tous tes canaux jusqu’à ce que tu relies un compte.',
    consent: 'Pour relier un compte plus tard, il faudra autoriser FXMate à nouveau.',
    confirm: 'Déconnecter le compte',
  },
  language: {
    heading: 'Langue',
    current: 'Français',
    note: 'Par défaut, la langue de ton app Telegram.',
  },
  invitedBy: { heading: 'Invité par' },
};

/** Shape of the `account` section of `Messages`. */
export type AccountMessages = typeof accountFr;

/** English texts of screen E11. `AccountMessages` makes a missing or extra key a compile error. */
export const accountEn: AccountMessages = {
  title: 'Account',
  loading: 'Loading…',
  retry: 'Try again',
  cancel: 'Cancel',
  telegram: {
    heading: 'Telegram',
    connected: 'Connected',
    disconnected: 'Disconnected',
    lost: 'Sign-in lost',
    disconnectedText: 'FXMate no longer reads your channels: copying is stopped.',
    unknownText:
      'Your Telegram connection status isn’t available while your trading account is unreachable.',
    logout: 'Log out of Telegram',
    signIn: 'Sign in again',
    loggedOut: 'You are logged out of Telegram. Copying is stopped.',
  },
  logoutSheet: {
    title: 'Log out of Telegram?',
    noRead: 'FXMate will no longer be able to read your channels.',
    stops_zero: 'All copying stops immediately.',
    stops_one: 'All copying stops immediately, on your channel.',
    stops_other: 'All copying stops immediately, on your {{count}} channels.',
    resume: 'To resume copying later, you will need to sign in again with a Telegram code.',
    confirm: 'Log out and stop copying',
  },
  openPositions_zero: 'You have no position opened by FXMate.',
  openPositions_one:
    'You have 1 position opened by FXMate: it stays open at your broker, FXMate does not close it.',
  openPositions_other:
    'You have {{count}} positions opened by FXMate: they stay open at your broker, FXMate does not close them.',
  tradingAccount: {
    heading: 'Trading account',
    summary: '{{platform}} · ••••{{last4}}',
    details: '{{server}} · {{holder}} · {{currency}}',
    demo: 'Demo account',
    consent: 'Orders authorized on {{date}} at {{time}}',
    none: 'No trading account',
    connect: 'Connect an account',
    pending: 'Your account connection is not finished.',
    finish: 'Finish connecting',
    reconnect: 'Reconnect my account',
    disconnect: 'Disconnect this account',
    disconnected: 'Trading account disconnected. Copying is stopped on all your channels.',
  },
  disconnectSheet: {
    title: 'Disconnect this account?',
    stops: 'Copying stops on all your channels until you connect an account.',
    consent: 'To connect an account later, you will need to authorize FXMate again.',
    confirm: 'Disconnect the account',
  },
  language: {
    heading: 'Language',
    current: 'English',
    note: 'By default, the language of your Telegram app.',
  },
  invitedBy: { heading: 'Invited by' },
};
